<script lang="ts">
  import { defineComponent, PropType } from 'vue'
  import Spinner from '@/components/Spinner.vue'

  type Variant = 'primary' | 'secondary' | 'danger'
  type Size = 'default' | 'small' | 'icon'
  type ButtonType = 'button' | 'submit'

  export default defineComponent({
    name: 'Button',

    components: { Spinner },

    props: {
      variant: {
        type: String as PropType<Variant>,
        default: 'primary',
        validator: (value: string) =>
          ['primary', 'secondary', 'danger'].includes(value),
      },
      size: {
        type: String as PropType<Size>,
        default: 'default',
        validator: (value: string) =>
          ['default', 'small', 'icon'].includes(value),
      },
      type: {
        type: String as PropType<ButtonType>,
        default: 'button',
        validator: (value: string) => ['button', 'submit'].includes(value),
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      // Shows a spinner and blocks further clicks. The label stays put: swapping it
      // makes the button jump width exactly when the user is watching it.
      loading: {
        type: Boolean,
        default: false,
      },
      active: {
        type: Boolean,
        default: false,
      },
      label: {
        type: String,
        default: null,
      },
    },

    emits: ['click'],
  })
</script>

<template>
  <button
    class="button"
    :class="[
      `button--${variant}`,
      {
        'button--small': size === 'small',
        'button--icon': size === 'icon',
        'is-active': active,
      },
    ]"
    :type="type"
    :disabled="disabled || loading"
    :aria-busy="loading ? 'true' : 'false'"
    :aria-label="label"
    :title="label"
    @click="$emit('click', $event)"
  >
    <Transition name="button-spinner">
      <span v-if="loading" class="button__spinner-slot" aria-hidden="true">
        <Spinner size="0.85em" />
      </span>
    </Transition>

    <slot></slot>
  </button>
</template>

<style lang="scss" scoped>
  @use '@/variables' as *;

  .button {
    display: inline-block;
    padding: 1rem 1.25rem;
    border: 1px solid transparent;
    border-radius: $default-border-radius;
    font-size: 0.9rem;
    font-weight: $font-weight-normal;
    line-height: 1;
    text-align: center;
    transition:
      background-color $transition-fast,
      border-color $transition-fast,
      color $transition-fast,
      box-shadow $transition-fast,
      opacity $transition-fast,
      transform $transition-fast;
    cursor: pointer;

    &:active {
      transform: translateY(1px);
    }

    &:focus-visible {
      outline: 0;
      box-shadow: $focus-ring;
    }

    &:disabled {
      pointer-events: none;
      opacity: 0.5;
      cursor: not-allowed;
    }

    // Kept at full opacity while busy: a half-faded spinner reads as broken
    // rather than as work in progress.
    &[aria-busy='true'] {
      opacity: 1;
    }

    &__spinner-slot {
      display: inline-block;
      width: 1.35em;
      height: 0.85em;
      overflow: hidden;
      vertical-align: -0.1em;
      white-space: nowrap;
    }

    .button-spinner-enter-active,
    .button-spinner-leave-active {
      transition:
        width $transition-fast,
        opacity $transition-fast;
    }

    .button-spinner-enter,
    .button-spinner-leave-to {
      width: 0;
      opacity: 0;
    }

    @media (prefers-reduced-motion: reduce) {
      .button-spinner-enter-active,
      .button-spinner-leave-active {
        transition: none;
      }
    }

    // Small — compact inline actions next to form labels
    &--small {
      padding: 0.5rem 0.75rem;
      font-size: 0.8rem;
    }

    // Primary — filled blue (default action)
    &--primary {
      color: $neutral-0;
      background-color: $primary-color;
      border-color: $primary-color;

      &:hover {
        background-color: lighten($primary-color, 10%);
        border-color: lighten($primary-color, 10%);
      }

      &.is-active {
        background-color: darken($primary-color, 10%);
        border-color: darken($primary-color, 10%);
        pointer-events: none;
      }
    }

    // Secondary — ghost / outline (cancel, back, reset)
    &--secondary {
      color: $primary-color;
      background-color: transparent;
      border-color: $primary-color;

      &:hover {
        background-color: rgba($primary-color, 0.1);
      }

      &.is-active {
        background-color: rgba($primary-color, 0.15);
        pointer-events: none;
      }
    }

    // Danger — filled red (delete, remove)
    &--danger {
      color: $neutral-0;
      background-color: $danger-color;
      border-color: $danger-color;

      &:focus-visible {
        box-shadow: 0 0 0 $focus-ring-width rgba($danger-color, 0.4);
      }

      &:hover {
        background-color: lighten($danger-color, 10%);
        border-color: lighten($danger-color, 10%);
      }
    }

    // Icon size — 33×33 square. No padding: the glyph is centred and sized
    // explicitly (see `.button--icon .lucide-icon` in App.vue), and inner
    // padding would otherwise flex-shrink the SVG down to a sliver.
    &--icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 33px;
      height: 33px;
      padding: 0;
    }
  }
</style>
